import { useCallback, useMemo, useState } from 'react';
import {
  SessionData,
  useAppService,
  useAuth,
  useBookingService,
  useConfig,
  usePersistSearchParams
} from '@ibe/components';
import { createUniqueId, SessionPersistence } from '@ibe/services';
import { setDefaultLocale } from 'react-datepicker';
import { useRouter, useSearchParams } from 'next/navigation';
import { HeaderProps } from './Header';
import dayjs from 'dayjs';
import { getLocale } from '@/Config/config';
import PageUrl from '@/Models/PageUrl';
import { Language, languages } from '@/app/i18n/settingsClient';
import { useTranslation } from '@/app/i18n/client';
import { useRemoveLangFromPath } from '@/Layouts/Header/useRemoveLangFromPath';

const useHeaderHooks = (props: HeaderProps) => {
  const { links } = props;
  const router = useRouter();
  const appService = useAppService();
  const bs = useBookingService();
  const searchParams = useSearchParams();
  const auth = useAuth();
  const config = useConfig();
  const { clearSearchParams } = usePersistSearchParams();
  const [showDesktop, setShowDesktop] = useState<boolean>(true);
  const { t } = useTranslation('Header');
  const linksKeys = useMemo(() => links.map((_, numb) => `${numb}_${createUniqueId()}`), []);

  const pathWithoutLang = useRemoveLangFromPath();

  const languageOptions = useMemo(() => {
    return languages.map(lang => {
      return {
        label: t(lang),
        value: lang
      };
    });
  }, [t]);

  const handleLanguageChange = useCallback(
    async (lang: string): Promise<void> => {
      appService.setLang(lang);
      if (bs.booking && bs.booking.bookedItems.length > 0) {
        await bs.doAttemptBooking();
      }
      router.push(
        `${appService.lang !== Language.NL ? '/' + appService.lang : ''}${pathWithoutLang}${
          searchParams.keys().some(key => key.length > 0)
            ? `?${new URLSearchParams(searchParams).toString()}`
            : ''
        }`
      );
      dayjs.locale(getLocale(lang));
      setDefaultLocale(appService.lang);
    },
    [appService, bs, router]
  );

  const logout = async () => {
    auth.removeToken();
    clearSearchParams();
    auth.authService.invalidate();
    bs.clearBooking();
    const session = new SessionPersistence<SessionData>(SessionData);
    session.useKey(config.sessionKeyCart);
    session.clear();
    router.push(PageUrl.HOME);
  };

  return {
    linksKeys,
    languageOptions,
    showDesktop,
    setShowDesktop,
    auth,
    logout,
    handleLanguageChange,
    config
  };
};

export default useHeaderHooks;
